import { Overview } from 'components/Overview'
import { removeNonPagePermissions } from 'hooks/hasPagePermissions'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { getServicingNotes } from 'services'
import Api from 'services/api'
import { setLoanNumber } from 'utils/setLoanNumber'

import { FciAttachments } from './FciAttachments'
import { FciBorrowerPayments } from './FciBorrowerPayments'
import { FciCharges } from './FciCharges'
import { FciFundingHistory } from './FciFundingHistory'
import { FciLoanDetail } from './FciLoanDetail'

const menus: Record<string, string> = {
  loanDetail: 'Loan Detail',
  borrowerPayments: 'Borrower Payments',
  charges: 'Charges',
  attachments: 'Attachments',
  fundingHistory: 'Funding History',
}
export default function Fci({ child = false }: { child?: boolean }) {
  const [selectedMenu, setSelectedMenu] = useState('loanDetail0')
  const [menuItems, setMenuItems] = useState<any>({})
  const [servicerCnt, setServicerCnt] = useState(1)

  const location = useLocation()
  const navigate = useHistory()

  useEffect(() => {
    setLoanNumber()
    removeNonPagePermissions(menus, ['pipeline', 'fci'])
    setMenuItems(menus)
    getServicingNotes().then((res) => {
      setServicerCnt(res.data['servicerLoanNumber2'] ? 2 : 1)
    })
  }, [])

  useEffect(() => {
    // Embedded (Servicing, the configurable page) the ?menu= belongs to the host screen and names
    // the host's entry, not one of ours - reading it here would select a menu that does not exist
    // and blank the content.
    if (child) return
    const menu = location.search.replace('?menu=', '')
    if (menu.length) setSelectedMenu(menu)
  }, [location, child])

  const setMenu = (menuItem: string) => {
    // Same reason: embedded, the route is the host's, so the submenu lives in local state. Pushing
    // /fci/:loanNumber would navigate the user off the screen they are on.
    if (child) {
      setSelectedMenu(menuItem)
      return
    }
    const loanNumber = Api.getLoanNumber()
    navigate.push(`/fci/${loanNumber}?menu=${menuItem}`)
  }

  const renderFragment = useMemo(() => {
    const idx = Number(selectedMenu[selectedMenu.length - 1])
    switch (selectedMenu) {
      case 'loanDetail0':
      case 'loanDetail1':
        return <FciLoanDetail servicerNo={idx} key={idx} />
      case 'borrowerPayments0':
      case 'borrowerPayments1':
        return <FciBorrowerPayments servicerNo={idx} key={idx} />
      case 'charges0':
      case 'charges1':
        return <FciCharges servicerNo={idx} key={idx} />
      case 'attachments0':
      case 'attachments1':
        return <FciAttachments servicerNo={idx} key={idx} />
      case 'fundingHistory0':
      case 'fundingHistory1':
        return <FciFundingHistory servicerNo={idx} key={idx} />
      default: {
        return <div></div>
      }
    }
  }, [selectedMenu])

  const renderMenu = (menus: Record<string, string>, servicerIdx: number) => {
    return (
      <ul className="sidebar-items flex flex-col p-4 pb-10">
        {Object.keys(menus).map((item: string, index) => {
          const isActive = selectedMenu === `${item}${servicerIdx}`
          return (
            <li
              key={index}
              onClick={() => {
                setMenu(`${item}${servicerIdx}`)
              }}
              className="py-2"
            >
              <p className={`hover:underline cursor-pointer ${isActive ? 'border px-4 py-1 bg-zinc-100' : 'py-1'}`}>
                {index + 1}. {menus[item]}
              </p>
            </li>
          )
        })}
      </ul>
    )
  }

  const title = menuItems[selectedMenu.slice(0, -1)]

  return (
    <div className={child ? '' : 'py-6 px-2'}>
      {!child && <Overview title="Fci" />}
      {/* Embedded, the host already draws the card the screen sits in, so the two panels carry a
          plain border instead of a second white card inside it. */}
      <div className={`grid grid-cols-12 md:gap-6 ${child ? '' : 'max-w-screen-2xl m-auto'}`}>
        <div
          className={`sidebar-left col-span-12 md:col-span-3 shrink-0 rounded mb-4 ${
            child ? 'border' : 'bg-white shadow1'
          }`}
        >
          <div className="p-4 -mb-6 font-bold">Servicer 1</div>
          {renderMenu(menuItems, 0)}
          {servicerCnt > 1 && (
            <>
              <div className="p-4 -mb-6 font-bold">Servicer 2</div>
              {renderMenu(menuItems, 1)}
            </>
          )}
        </div>
        <div
          className={`relative h-fit content-right col-span-12 md:col-span-9 rounded mb-4 ${
            child ? '' : 'bg-white p-4 shadow1'
          }`}
        >
          <h2 className="text-2xl font-bold flex items-center mb-5">{child && title ? `FCI - ${title}` : title}</h2>
          {renderFragment}
        </div>
      </div>
    </div>
  )
}
